import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '../contexts/SocketContext';

export interface UseRelayResult<TState> {
  state: TState | null;
  startRelay: (gameId: string, initialState: TState) => void;
  sendAction: (action: unknown, newState: TState) => void;
  // A connection problem with your last move: "saving" while it waits for the server, "lost" when it
  // couldn't be saved and the game went back to where the server has it. null when all is well.
  sync: 'saving' | 'lost' | null;
}

// Moves show at once (optimistic) and the server confirms each one. A move it hasn't confirmed is kept;
// after a dropped connection it is sent again, if the server's game is still where the move started
// from. Otherwise the server's game wins and the page says your move was lost.
interface Pending<TState> { base: TState; action: unknown; state: TState; seq: number }
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function useRelay<TState>(slug: string): UseRelayResult<TState> {
  const { socket, isConnected } = useSocket();
  const [state, setState] = useState<TState | null>(null);
  const [sync, setSync] = useState<'saving' | 'lost' | null>(null);
  const stateRef = useRef<TState | null>(null);
  const pending = useRef<Pending<TState> | null>(null);
  const seq = useRef(0);
  const retry = useRef<number | undefined>(undefined);
  const tries = useRef(0);
  const set = useCallback((s: TState) => { stateRef.current = s; setState(s); }, []);

  // Ask for the server's game; with a move waiting, its reply decides whether to send the move again.
  const check = useCallback(() => {
    window.clearTimeout(retry.current);
    if (socket?.connected) socket.emit('relay:request-state', { slug });
    else retry.current = window.setTimeout(() => check(), 2000);
  }, [socket, slug]);

  const send = useCallback((p: Pending<TState>) => {
    if (!socket) return;
    window.clearTimeout(retry.current);
    // No answer in a few seconds: say so, and check again.
    retry.current = window.setTimeout(() => { setSync('saving'); check(); }, 4000);
    socket.emit('relay:action', { slug, action: p.action, state: p.state }, (r: { ok: boolean } | undefined) => {
      if (pending.current?.seq !== p.seq) return;
      if (r?.ok) { pending.current = null; tries.current = 0; window.clearTimeout(retry.current); setSync(null); return; }
      // Not taken (e.g. not back in the room yet after reconnecting): try again shortly.
      window.clearTimeout(retry.current);
      retry.current = window.setTimeout(() => check(), 1500);
    });
  }, [socket, slug, check]);

  // Request current state whenever we (re)connect — handles reconnects and late joins
  useEffect(() => {
    if (!socket || !isConnected) return;
    socket.emit('relay:request-state', { slug });
  }, [socket, isConnected, slug]);

  useEffect(() => {
    if (!socket) return;

    const onStarted = ({ state }: { gameId: string; state: TState }) => { pending.current = null; setSync(null); set(state); };
    const onState = ({ state: s, fromPlayerId }: { action: unknown; state: TState; fromPlayerId: string | null }) => {
      const p = pending.current;
      if (p) {
        if (fromPlayerId === socket.id) return;   // our own move echoed back; the ack settles it
        if (fromPlayerId === null && same(s, p.base) && tries.current < 10) {
          // The server is where our move started: send it again and keep showing it.
          tries.current++;
          setSync('saving');
          send(p);
          return;
        }
        if (fromPlayerId === null && same(s, p.state)) { pending.current = null; tries.current = 0; setSync(null); set(s); return; }
        // Someone else moved, or the server has something else: its game wins.
        pending.current = null;
        tries.current = 0;
        window.clearTimeout(retry.current);
        setSync('lost');
      }
      set(s);
    };

    socket.on('relay:started', onStarted);
    socket.on('relay:state', onState);
    return () => {
      socket.off('relay:started', onStarted);
      socket.off('relay:state', onState);
    };
  }, [socket, send, set]);

  useEffect(() => () => window.clearTimeout(retry.current), []);

  const startRelay = useCallback((gameId: string, initialState: TState) => {
    set(initialState); // optimistic update
    socket?.emit('relay:start', { slug, gameId, state: initialState });
  }, [socket, slug, set]);

  const sendAction = useCallback((action: unknown, newState: TState) => {
    const base = pending.current?.base ?? stateRef.current ?? newState;
    const p = { base, action, state: newState, seq: ++seq.current };
    pending.current = p;
    if (sync === 'lost') setSync(null);
    set(newState); // optimistic update
    send(p);
  }, [send, sync, set]);

  return { state, startRelay, sendAction, sync };
}
